import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { BellRing, Sparkles, X } from 'lucide-react';
import { useAuth } from '@/auth/AuthProvider';
import { Button, Modal, useToast } from '@/components/ui';
import { latestFor, mandatoryFor, markWhatsNewSeen, readWhatsNewSeen } from '@/help/changelog';
import { ApiError, api } from '@/lib/api';
import { PREFS_KEY, usePreferences } from '@/lib/preferences';
import type { MyPreferences } from '@/lib/types';

/** بطاقةُ «ما الجديد» في الصفحة الأولى — مرّةً لكل إصدار، وبما يخصّ صفاتك وحدها (`help/changelog.ts`) */
export function WhatsNew() {
  const { roles } = useAuth();
  const prefs = usePreferences();
  const latest = latestFor(roles);
  const [seen, setSeen] = useState(readWhatsNewSeen);
  // ما اطُّلع عليه في نافذة الإدارة الإلزامية (على هذا الجهاز أو غيره) لا يُعاد بطاقةً
  if (!latest || seen === latest.id || prefs.data?.whatsNew.id === latest.id) return null;
  const close = () => { markWhatsNewSeen(latest.id); setSeen(latest.id); };
  return (
    <section className="whats-new" aria-labelledby="whats-new-title" data-testid="whats-new">
      <div className="whats-new__head">
        <Sparkles size={18} aria-hidden />
        <h2 id="whats-new-title" className="whats-new__title grow">ما الجديد</h2>
        <button type="button" className="icon-btn" onClick={close} aria-label="أخفِ «ما الجديد»"><X size={16} aria-hidden /></button>
      </div>
      <ul className="whats-new__list">
        {latest.items.map((i) => <li key={i.title}><strong>{i.title}</strong><span>{i.text}</span></li>)}
      </ul>
    </section>
  );
}

/**
 * إشعارُ التحديث الإلزامي لحسابات الإدارة (0087 — 16 أكتوبر 2026): مع كل إصدارٍ جديد في `help/changelog.ts` نافذةٌ في أي صفحة
 * لا تُغلق إلا بـ«اطّلعت»، والاطّلاعُ يُكتب في الخادم فلا تعود على جهازٍ آخر. وإن تعذّر الحفظ تُغلق لهذه الزيارة وتعود في
 * التالية — نافذةٌ لا تُغلق والخادمُ لا يجيب تحبس المديرَ خارج عمله.
 */
export function MandatoryUpdateNotice() {
  const { roles } = useAuth();
  const prefs = usePreferences();
  const qc = useQueryClient();
  const toast = useToast();
  const [skipped, setSkipped] = useState<string | null>(null);
  const due = prefs.data ? mandatoryFor(roles, prefs.data.whatsNew.id) : null;
  const ack = useMutation({
    mutationFn: (id: string) => api.post<MyPreferences>('/me/preferences/whats-new', { id }, { institutionId: null }),
    onSuccess: (p, id) => { markWhatsNewSeen(id); qc.setQueryData(PREFS_KEY, p); },
    onError: (e, id) => {
      setSkipped(id);
      toast.push('error', e instanceof ApiError ? `لم يُحفظ اطّلاعك: ${e.message} — ستظهر النافذة في زيارتك التالية.` : 'لم يُحفظ اطّلاعك — ستظهر النافذة في زيارتك التالية.');
    },
  });
  if (!due || skipped === due.id) return null;
  return (
    <Modal
      dismissible={false}
      title="تحديثٌ جديد في المنصة"
      description="وصل المنصةَ تحديثٌ يخصّ حسابك — اقرأ ما تغيّر ثم أكّد اطّلاعك."
      onClose={() => undefined}
      footer={<Button variant="primary" icon={<BellRing size={16} />} loading={ack.isPending} onClick={() => ack.mutate(due.id)}>اطّلعت على التحديث</Button>}
    >
      <ul className="whats-new__list" data-testid="mandatory-update">
        {due.items.map((i) => <li key={i.title}><strong>{i.title}</strong><span>{i.text}</span></li>)}
      </ul>
    </Modal>
  );
}
